import { useEffect, useState } from "react";
import TimeSelect from "../TimeSelect";
import InfoTooltip from "../common/InfoTooltip";
import ImageUploadField from "../ImageUploadField";
import { formatNANP, isValidNANP, toNANPDigits10 } from "../../utils/phone";
import {
  SKILL_OPTIONS,
  LANGUAGE_OPTIONS,
  YEARS_OPTIONS,
} from "../../constants/technicianProfileOptions";

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const defaultWeeklySchedule = () =>
  Array.from({ length: 7 }).map((_, idx) => ({
    day_of_week: idx,
    is_working: false,
    start_time: null,
    end_time: null,
  }));

const snapTo15 = (hhmm) => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(":");
  const hh = Number(h);
  const mm = Number(m);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  let snapped = Math.round(mm / 15) * 15;
  let hour = hh;
  if (snapped === 60) {
    snapped = 0;
    hour = (hour + 1) % 24;
  }
  return `${String(hour).padStart(2, "0")}:${String(snapped).padStart(2, "0")}`;
};

const splitFullName = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return { first_name: "", last_name: "" };
  }
  const parts = normalized.split(/\s+/);
  return {
    first_name: parts[0] || "",
    last_name: parts.slice(1).join(" "),
  };
};

const createDefaultProfile = () => ({
  display_name: "",
  first_name: "",
  last_name: "",
  role: "technician",
  email: "",
  phone: "",
  seniority_label: "",
  price_adjustment_type: "",
  price_adjustment_value: "",
  display_order: "",
  skills: [],
  skills_other: "",
  languages: [],
  languages_other: "",
  years_experience: "",
  years_experience_other: "",
  is_certified: null,
  notes: "",
  photo_url: "",
  uses_time_clock: false,
  show_in_online_booking: true,
  employment_start_date: "",
});

const CERTIFICATION_OPTIONS = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

const DEFAULT_ROLE_OPTIONS = [
  { value: "technician", label: "Technician" },
  { value: "host", label: "Host" },
  { value: "front_desk", label: "Front Desk" },
  { value: "manager", label: "Manager" },
];
const TECHNICIAN_PERMISSION_ROLES = new Set([
  "technician",
  "apprentice",
  "solo_technician",
]);
const SENIORITY_LABEL_OPTIONS = [
  { value: "", label: "None" },
  { value: "apprentice", label: "Apprentice" },
  { value: "junior", label: "Junior" },
  { value: "senior", label: "Senior" },
  { value: "coach", label: "Coach" },
  { value: "master", label: "Master" },
];
const SENIORITY_LABEL_TOOLTIP_TEXT = [
  "Apprentice - 0-3 months experience",
  "Junior - 4-18 months experience",
  "Senior - 2-4 years experience",
  "Coach - Senior technician who mentors or trains others (typically 3-5+ years plus coaching responsibility)",
  "Master - 5+ years experience and more",
].join("\n");
const PRICE_ADJUSTMENT_TYPE_OPTIONS = [
  { value: "", label: "None" },
  { value: "amount", label: "Amount ($)" },
  { value: "percent", label: "Percent (%)" },
];
const TECHNICIAN_PRICE_ADJUSTMENT_TOOLTIP_TEXT =
  "Optional: add a premium or discount applied to each service when booking with this technician. Use either a fixed amount (for example 5 or -5) or a percent (for example 10% or -10%).";

export default function StaffEditorForm({
  mode = "create",
  initialStaff = null,
  initialSchedule = null,
  onSave,
  onSaveSchedule,
  saving = false,
  savingSchedule = false,
  error = "",
  showCancel = false,
  onCancel,
  compact = false,
  roleOptions = DEFAULT_ROLE_OPTIONS,
  roleLocked = false,
  roleLockNote = "",
  canEditScheduleOrder = false,
  onUploadPhoto,
  onRemovePhoto,
  beforeProfileContent = null,
  uiClasses = {},
  highlightSectionId = "",
  showInlineHelp = false,
  showTechnicianPermissionToggles = false,
  permissionToggles = null,
  loadingPermissionToggles = false,
  canEditUsesTimeClock = true,
  canEditOnlineBookingVisibility = true,
}) {
  const [form, setForm] = useState(createDefaultProfile());
  const [weeklySchedule, setWeeklySchedule] = useState(defaultWeeklySchedule());
  const [phoneError, setPhoneError] = useState("");
  const [detailPermissionToggles, setDetailPermissionToggles] = useState({
    enableCancelAppointments: false,
    enableEstimatedRevenue: false,
    enableSalonSpeakModules: false,
  });

  useEffect(() => {
    if (initialStaff) {
      const nameParts = splitFullName(initialStaff.full_name);
      setForm({
        display_name: initialStaff.display_name || "",
        first_name: initialStaff.first_name || nameParts.first_name,
        last_name: initialStaff.last_name || nameParts.last_name,
        role: initialStaff.role || "technician",
        email: initialStaff.email || "",
        phone: toNANPDigits10(initialStaff.phone || ""),
        seniority_label: initialStaff.seniority_label || "",
        price_adjustment_type: initialStaff.price_adjustment_type || "",
        price_adjustment_value:
          initialStaff.price_adjustment_value === null ||
          initialStaff.price_adjustment_value === undefined
            ? ""
            : String(initialStaff.price_adjustment_value),
        display_order:
          initialStaff.display_order === null || initialStaff.display_order === undefined
            ? ""
            : String(initialStaff.display_order),
        skills: Array.isArray(initialStaff.skills) ? initialStaff.skills : [],
        skills_other: initialStaff.skills_other || "",
        languages: Array.isArray(initialStaff.languages) ? initialStaff.languages : [],
        languages_other: initialStaff.languages_other || "",
        years_experience: initialStaff.years_experience || "",
        years_experience_other: initialStaff.years_experience_other || "",
        is_certified:
          typeof initialStaff.is_certified === "boolean"
            ? initialStaff.is_certified
            : null,
        notes: initialStaff.notes || "",
        photo_url: initialStaff.photo_url || "",
        uses_time_clock: Boolean(initialStaff.uses_time_clock),
        show_in_online_booking:
          initialStaff.show_in_online_booking === false ? false : true,
        employment_start_date: initialStaff.employment_start_date || "",
      });
    } else {
      setForm(createDefaultProfile());
    }
  }, [initialStaff]);

  useEffect(() => {
    if (Array.isArray(initialSchedule) && initialSchedule.length) {
      setWeeklySchedule(
        initialSchedule.map((item) => ({
          day_of_week: item.day_of_week,
          is_working: !!item.is_working,
          start_time: snapTo15(item.start_time) || null,
          end_time: snapTo15(item.end_time) || null,
        }))
      );
    } else {
      setWeeklySchedule(defaultWeeklySchedule());
    }
  }, [initialSchedule]);

  useEffect(() => {
    setDetailPermissionToggles({
      enableCancelAppointments: Boolean(permissionToggles?.enableCancelAppointments),
      enableEstimatedRevenue: Boolean(permissionToggles?.enableEstimatedRevenue),
      enableSalonSpeakModules: Boolean(permissionToggles?.enableSalonSpeakModules),
    });
  }, [
    permissionToggles?.enableCancelAppointments,
    permissionToggles?.enableEstimatedRevenue,
    permissionToggles?.enableSalonSpeakModules,
  ]);

  const toggleSelection = (field, value) => {
    setForm((prev) => {
      const exists = prev[field].includes(value);
      const updated = exists
        ? prev[field].filter((item) => item !== value)
        : [...prev[field], value];
      return { ...prev, [field]: updated };
    });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!form.display_name.trim() || typeof onSave !== "function") return;
    const phoneDigits = form.phone ? toNANPDigits10(form.phone) : "";
    if (phoneDigits && !isValidNANP(phoneDigits)) {
      setPhoneError("Phone must be a 10-digit North American number.");
      return;
    }
    setPhoneError("");
    const orderValue = form.display_order?.toString().trim();
    let displayOrderPayload = null;
    if (orderValue) {
      const parsed = Number(orderValue);
      if (Number.isFinite(parsed)) {
        displayOrderPayload = Math.max(0, Math.floor(parsed));
      }
    }
    const normalizedAdjustmentType = (form.price_adjustment_type || "")
      .trim()
      .toLowerCase();
    const parsedAdjustmentValue = Number(form.price_adjustment_value);
    const payload = {
      display_name: form.display_name.trim(),
      email: form.email ? form.email : null,
      phone: phoneDigits || null,
      role: form.role,
      seniority_label: form.seniority_label ? form.seniority_label : null,
      price_adjustment_type: normalizedAdjustmentType || null,
      price_adjustment_value:
        normalizedAdjustmentType && Number.isFinite(parsedAdjustmentValue)
          ? parsedAdjustmentValue
          : null,
      display_order: canEditScheduleOrder ? displayOrderPayload : undefined,
      skills: form.skills,
      skills_other: form.skills.includes("Other")
        ? form.skills_other || null
        : null,
      languages: form.languages,
      languages_other: form.languages.includes("Other")
        ? form.languages_other || null
        : null,
      years_experience: form.years_experience || null,
      years_experience_other:
        form.years_experience === "Other" ? form.years_experience_other || null : null,
      is_certified: form.is_certified,
      notes: form.notes || null,
      photo_url: form.photo_url ? form.photo_url.trim() : null,
      uses_time_clock: Boolean(form.uses_time_clock),
      show_in_online_booking: Boolean(form.show_in_online_booking),
      employment_start_date: form.employment_start_date || null,
    };
    if (mode === "edit") {
      payload.first_name = form.first_name.trim();
      payload.last_name = form.last_name.trim();
    }
    const showPermissionTogglesForRole =
      showTechnicianPermissionToggles &&
      TECHNICIAN_PERMISSION_ROLES.has((form.role || "").toLowerCase());
    await onSave({
      info: payload,
      schedule: weeklySchedule,
      permissionToggles: showPermissionTogglesForRole
        ? {
            enableCancelAppointments: Boolean(
              detailPermissionToggles.enableCancelAppointments
            ),
            enableEstimatedRevenue: Boolean(
              detailPermissionToggles.enableEstimatedRevenue
            ),
            enableSalonSpeakModules: Boolean(
              detailPermissionToggles.enableSalonSpeakModules
            ),
          }
        : null,
    });
  };

  const handleScheduleSave = async () => {
    if (typeof onSaveSchedule !== "function") return;
    await onSaveSchedule(weeklySchedule);
  };

  const handleScheduleChange = (dayIdx, field, value) => {
    setWeeklySchedule((prev) =>
      prev.map((item) =>
        item.day_of_week === dayIdx ? { ...item, [field]: value } : item
      )
    );
  };

  const handleToggleWorking = (dayIdx, checked) => {
    setWeeklySchedule((prev) =>
      prev.map((item) => {
        if (item.day_of_week !== dayIdx) {
          return item;
        }
        if (checked) {
          const start = item.start_time || "09:00";
          const end = item.end_time || "17:00";
          return {
            ...item,
            is_working: true,
            start_time: snapTo15(start) || "09:00",
            end_time: snapTo15(end) || "17:00",
          };
        }
        return { ...item, is_working: false };
      })
    );
  };

  const hasUiClasses = Object.keys(uiClasses || {}).length > 0;
  const modalText = hasUiClasses ? uiClasses.modalText : "text-sm";
  const labelText = hasUiClasses ? uiClasses.labelText : "text-sm text-slate-700";
  const fieldClass = hasUiClasses
    ? uiClasses.fieldClass
    : "border border-slate-300 rounded px-3 py-2 text-sm";
  const btnBase = hasUiClasses ? uiClasses.btnBase : "px-4 py-2 rounded text-sm";
  const cardClass = hasUiClasses
    ? uiClasses.cardClass
    : "border rounded-lg bg-white shadow-sm p-4";
  const sectionTitleClass = hasUiClasses
    ? "text-lg md:text-xl font-semibold text-slate-900"
    : "text-lg font-semibold text-slate-900";
  const focusClass = hasUiClasses
    ? "focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
    : "";
  const textareaClass = hasUiClasses
    ? "min-h-[120px] w-full rounded-xl border border-slate-300 px-4 py-3 text-base md:text-lg text-slate-700"
    : "min-h-[90px] w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700";
  const primaryBtnClass = hasUiClasses
    ? `${btnBase} font-semibold text-white transition bg-slate-900 hover:bg-slate-800 disabled:opacity-60`
    : "px-4 py-2 rounded bg-slate-900 text-white text-sm font-semibold disabled:opacity-60";
  const secondaryBtnClass = hasUiClasses
    ? `${btnBase} border border-slate-300 font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50`
    : "px-4 py-2 rounded border border-slate-300 text-sm text-slate-700";
  const spacingClass = compact ? "space-y-4" : "space-y-6";
  const invertedSpacing = compact ? "gap-2" : "gap-4";
  const saveLabel = mode === "edit" ? "Save Changes" : "Add Staff";
  const highlightedSectionClass =
    "ring-2 ring-rose-300 ring-offset-2 transition-shadow duration-200";
  const showPermissionTogglesForRole =
    showTechnicianPermissionToggles &&
    TECHNICIAN_PERMISSION_ROLES.has((form.role || "").toLowerCase());
  const tabTipClass =
    "mb-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700";
  const sectionClassName = (id) =>
    `${cardClass} scroll-mt-24 ${highlightSectionId === id ? highlightedSectionClass : ""}`.trim();
  const handlePhotoUpload = async (file) => {
    if (typeof onUploadPhoto !== "function") {
      return form.photo_url || "";
    }
    return onUploadPhoto(file);
  };
  const handlePhotoRemove = async () => {
    if (typeof onRemovePhoto !== "function") {
      return "";
    }
    return onRemovePhoto();
  };


  return (
    <div className={`${spacingClass} ${modalText}`}>
      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm">
          {error}
        </div>
      )}

      <div id="section-details" className={sectionClassName("section-details")}>
        <div className="mb-4">
          <div className="flex items-center gap-2">
            <h3 className={sectionTitleClass}>Staff Details</h3>
            {showInlineHelp && (
              <InfoTooltip
                text="Main profile information for this staff member."
                label="Staff Details"
                maxWidth={260}
              />
            )}
          </div>
          {showInlineHelp && (
            <p className={tabTipClass}>
              Tip: Keep the display name and role accurate so schedule visibility and reports stay reliable.
            </p>
          )}
        </div>
        <form className="space-y-4" onSubmit={handleSave}>
          <ImageUploadField
            label="Staff Photo"
            helper="Shown before staff names in the schedule."
            recommended="Use a square image for best fit."
            value={form.photo_url}
            onChange={(next) =>
              setForm((prev) => ({ ...prev, photo_url: next || "" }))
            }
            onUpload={handlePhotoUpload}
            onRemove={handlePhotoRemove}
          />
          {mode === "edit" && (
            <div className={`grid sm:grid-cols-2 ${invertedSpacing}`}>
              <label htmlFor="staff_first_name" className="flex flex-col gap-1">
                <span className={`${labelText} uppercase flex items-center gap-2`}>
                  First Name
                  {showInlineHelp && (
                    <InfoTooltip
                      text="Staff member's first name."
                      label="First Name"
                      maxWidth={240}
                    />
                  )}
                </span>
                <input
                  id="staff_first_name"
                  name="staff_first_name"
                  type="text"
                  value={form.first_name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, first_name: e.target.value }))
                  }
                  className={`w-full ${fieldClass} ${focusClass}`.trim()}
                />
              </label>
              <label htmlFor="staff_last_name" className="flex flex-col gap-1">
                <span className={`${labelText} uppercase flex items-center gap-2`}>
                  Last Name
                  {showInlineHelp && (
                    <InfoTooltip
                      text="Staff member's last name."
                      label="Last Name"
                      maxWidth={240}
                    />
                  )}
                </span>
                <input
                  id="staff_last_name"
                  name="staff_last_name"
                  type="text"
                  value={form.last_name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, last_name: e.target.value }))
                  }
                  className={`w-full ${fieldClass} ${focusClass}`.trim()}
                />
              </label>
            </div>
          )}
          <div className={`grid sm:grid-cols-2 ${invertedSpacing}`}>
            <label htmlFor="staff_display_name" className="flex flex-col gap-1">
              <span className={`${labelText} uppercase flex items-center gap-2`}>
                Display Name
                {showInlineHelp && (
                  <InfoTooltip
                    text="Name shown on schedules, tickets, and booking screens."
                    label="Display Name"
                    maxWidth={260}
                  />
                )}
              </span>
              <input
                id="staff_display_name"
                name="staff_display_name"
                type="text"
                value={form.display_name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, display_name: e.target.value }))
                }
                className={`w-full ${fieldClass} ${focusClass}`.trim()}
                required
              />
            </label>
            <label htmlFor="staff_role" className="flex flex-col gap-1">
              <span className={`${labelText} uppercase flex items-center gap-2`}>
                Role
                {showInlineHelp && (
                  <InfoTooltip
                    text="Controls access and what this staff member can do."
                    label="Role"
                    maxWidth={260}
                  />
                )}
              </span>
              <select
                id="staff_role"
                name="staff_role"
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                className={`w-full ${fieldClass} ${focusClass}`.trim()}
                disabled={roleLocked}
              >
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {roleLockNote ? (
                <span className="text-xs text-slate-500">{roleLockNote}</span>
              ) : null}
            </label>
          </div>
          <div className={`grid sm:grid-cols-2 ${invertedSpacing}`}>
            <label htmlFor="staff_email" className="flex flex-col gap-1">
              <span className={`${labelText} uppercase flex items-center gap-2`}>
                Email
                {showInlineHelp && (
                  <InfoTooltip
                    text="Contact email for this staff member."
                    label="Staff Email"
                    maxWidth={260}
                  />
                )}
              </span>
              <input
                id="staff_email"
                name="staff_email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className={`w-full ${fieldClass} ${focusClass}`.trim()}
              />
            </label>
            <label htmlFor="staff_phone" className="flex flex-col gap-1">
              <span className={`${labelText} uppercase flex items-center gap-2`}>
                Phone
                {showInlineHelp && (
                  <InfoTooltip
                    text="Contact phone number for this staff member."
                    label="Phone"
                    maxWidth={260}
                  />
                )}
              </span>
              <input
                id="staff_phone"
                name="staff_phone"
                type="text"
                value={isValidNANP(form.phone) ? formatNANP(form.phone) : form.phone}
                onChange={(e) => {
                  setForm((f) => ({
                    ...f,
                    phone: toNANPDigits10(e.target.value),
                  }));
                  if (phoneError) {
                    setPhoneError("");
                  }
                }}
                className={`w-full ${fieldClass} ${focusClass}`.trim()}
              />
              {phoneError && (
                <span className="text-xs text-rose-600">{phoneError}</span>
              )}
            </label>
          </div>
          <label htmlFor="staff_employment_start_date" className="flex flex-col gap-1">
            <span className={`${labelText} uppercase flex items-center gap-2`}>
              Hiring Date
              {showInlineHelp && (
                <InfoTooltip
                  text="Optional date used for compensation and payroll reference."
                  label="Hiring Date"
                  maxWidth={280}
                />
              )}
            </span>
            <input
              id="staff_employment_start_date"
              name="staff_employment_start_date"
              type="date"
              value={form.employment_start_date}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  employment_start_date: e.target.value,
                }))
              }
              className={`w-full ${fieldClass} ${focusClass}`.trim()}
            />
          </label>
          <label htmlFor="staff_seniority_label" className="flex flex-col gap-1">
            <span className={`${labelText} uppercase flex items-center gap-2`}>
              Technician Label
              <InfoTooltip
                text={SENIORITY_LABEL_TOOLTIP_TEXT}
                label="Technician Label"
                maxWidth={340}
              />
            </span>
            <select
              id="staff_seniority_label"
              name="staff_seniority_label"
              value={form.seniority_label}
              onChange={(e) =>
                setForm((f) => ({ ...f, seniority_label: e.target.value }))
              }
              className={`w-full ${fieldClass} ${focusClass}`.trim()}
            >
              {SENIORITY_LABEL_OPTIONS.map((option) => (
                <option key={option.value || "none"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <div className="flex flex-col gap-1">
            <span className={`${labelText} uppercase flex items-center gap-2`}>
              Technician Price Adjustment
              <InfoTooltip
                text={TECHNICIAN_PRICE_ADJUSTMENT_TOOLTIP_TEXT}
                label="Technician Price Adjustment"
                maxWidth={360}
              />
            </span>
            <div className={`grid sm:grid-cols-2 ${invertedSpacing}`}>
              <select
                id="staff_price_adjustment_type"
                name="staff_price_adjustment_type"
                value={form.price_adjustment_type}
                onChange={(e) => {
                  const nextType = e.target.value;
                  setForm((f) => ({
                    ...f,
                    price_adjustment_type: nextType,
                    price_adjustment_value: nextType ? f.price_adjustment_value : "",
                  }));
                }}
                className={`w-full ${fieldClass} ${focusClass}`.trim()}
              >
                {PRICE_ADJUSTMENT_TYPE_OPTIONS.map((option) => (
                  <option key={option.value || "none"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="relative">
                {form.price_adjustment_type === "amount" && (
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                    $
                  </span>
                )}
                <input
                  id="staff_price_adjustment_value"
                  name="staff_price_adjustment_value"
                  type="number"
                  step="0.01"
                  disabled={!form.price_adjustment_type}
                  value={form.price_adjustment_value}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      price_adjustment_value: e.target.value,
                    }))
                  }
                  placeholder={
                    form.price_adjustment_type === "percent"
                      ? "e.g. 10 or -10"
                      : "e.g. 5 or -5"
                  }
                  className={`w-full ${fieldClass} ${focusClass} ${
                    form.price_adjustment_type === "amount" ? "pl-7" : ""
                  } ${
                    form.price_adjustment_type === "percent" ? "pr-7" : ""
                  } disabled:bg-slate-100 disabled:text-slate-400`.trim()}
                />
                {form.price_adjustment_type === "percent" && (
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">
                    %
                  </span>
                )}
              </div>
            </div>
          </div>
          {showPermissionTogglesForRole && (
            <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="flex items-center gap-2">
                <p className="text-sm md:text-base font-semibold text-slate-900">
                  Technician Permission Shortcuts
                </p>
                {showInlineHelp && (
                  <InfoTooltip
                    text="Quick switches for common technician access."
                    label="Technician Permission Shortcuts"
                    maxWidth={280}
                  />
                )}
              </div>
              {loadingPermissionToggles ? (
                <p className="text-sm text-slate-500">Loading current permissions...</p>
              ) : (
                <div className="space-y-2">
                  <label className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm md:text-base text-slate-700">
                    <span>Turn on Show cancelled appointments</span>
                    <input
                      type="checkbox"
                      checked={!!detailPermissionToggles.enableCancelAppointments}
                      onChange={(e) =>
                        setDetailPermissionToggles((prev) => ({
                          ...prev,
                          enableCancelAppointments: e.target.checked,
                        }))
                      }
                      className="h-4 w-4 rounded border border-slate-300 text-slate-900 focus:ring-0"
                    />
                  </label>
                  <label className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm md:text-base text-slate-700">
                    <span>Turn on View estimated revenue</span>
                    <input
                      type="checkbox"
                      checked={!!detailPermissionToggles.enableEstimatedRevenue}
                      onChange={(e) =>
                        setDetailPermissionToggles((prev) => ({
                          ...prev,
                          enableEstimatedRevenue: e.target.checked,
                        }))
                      }
                      className="h-4 w-4 rounded border border-slate-300 text-slate-900 focus:ring-0"
                    />
                  </label>
                  <label className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm md:text-base text-slate-700">
                    <span>Turn on View assigned training (for SalonSpeak modules)</span>
                    <input
                      type="checkbox"
                      checked={!!detailPermissionToggles.enableSalonSpeakModules}
                      onChange={(e) =>
                        setDetailPermissionToggles((prev) => ({
                          ...prev,
                          enableSalonSpeakModules: e.target.checked,
                        }))
                      }
                      className="h-4 w-4 rounded border border-slate-300 text-slate-900 focus:ring-0"
                    />
                  </label>
                </div>
              )}
            </div>
          )}
          {canEditScheduleOrder && (
            <label htmlFor="staff_display_order" className="flex flex-col gap-1">
              <span className={`${labelText} uppercase flex items-center gap-2`}>
                Schedule Order (optional)
                {showInlineHelp && (
                  <InfoTooltip
                    text="Controls where this staff appears in schedule columns."
                    label="Schedule Order"
                    maxWidth={280}
                  />
                )}
              </span>
              <input
                id="staff_display_order"
                name="staff_display_order"
                type="number"
                step="1"
                value={form.display_order}
                onChange={(e) =>
                  setForm((f) => ({ ...f, display_order: e.target.value }))
                }
                className={`w-full ${fieldClass} ${focusClass}`.trim()}
              />
            </label>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={saving}
              className={primaryBtnClass}
            >
              {saving ? "Saving..." : saveLabel}
            </button>
            {showCancel && onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className={secondaryBtnClass}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div
        id="section-settings"
        className={`${sectionClassName("section-settings")} space-y-3`}
      >
        <div className="mb-4">
          <div className="flex items-center gap-2">
            <h3 className={sectionTitleClass}>Settings</h3>
            {showInlineHelp && (
              <InfoTooltip
                text="Staff options that affect manager tools."
                label="Settings"
                maxWidth={260}
              />
            )}
          </div>
          <p className="text-sm md:text-base text-slate-500">
            Control staff-level features that affect manager tools.
          </p>
          {showInlineHelp && (
            <p className={tabTipClass}>
              Tip: Deactivate or adjust settings instead of deleting staff, so history and reporting stay intact.
            </p>
          )}
        </div>
        <label className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm md:text-base text-slate-700">
          <span className="flex items-center gap-2 font-semibold text-slate-900">
            Uses Time Clock
            {showInlineHelp && (
              <InfoTooltip
                text="Allows this staff member to use time clock features."
                label="Uses Time Clock"
                maxWidth={260}
              />
            )}
          </span>
          <input
            type="checkbox"
            checked={!!form.uses_time_clock}
            disabled={!canEditUsesTimeClock}
            onChange={(e) =>
              setForm((f) => ({ ...f, uses_time_clock: e.target.checked }))
            }
            className="h-4 w-4 rounded border border-slate-300 text-slate-900 focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
          />
        </label>
        {!canEditUsesTimeClock && (
          <p className="text-xs md:text-sm text-slate-500">
            Ask an owner or manager to turn on Uses Time Clock if you need to change this setting.
          </p>
        )}
        <label className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm md:text-base text-slate-700">
          <span className="flex items-center gap-2 font-semibold text-slate-900">
            Show in Online Booking
            {showInlineHelp && (
              <InfoTooltip
                text="Shows this staff member as an option in online booking when their services and schedule allow it."
                label="Show in Online Booking"
                maxWidth={300}
              />
            )}
          </span>
          <input
            type="checkbox"
            checked={!!form.show_in_online_booking}
            disabled={!canEditOnlineBookingVisibility}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                show_in_online_booking: e.target.checked,
              }))
            }
            className="h-4 w-4 rounded border border-slate-300 text-slate-900 focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
          />
        </label>
        {!canEditOnlineBookingVisibility && (
          <p className="text-xs md:text-sm text-slate-500">
            Ask an owner or manager to turn on Show in Online Booking if you need to change this setting.
          </p>
        )}
      </div>

      {beforeProfileContent}

      <div id="section-profile" className={sectionClassName("section-profile")}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2">
              <h2 className={sectionTitleClass}>Staff Profile (Optional)</h2>
              {showInlineHelp && (
                <InfoTooltip
                  text="Optional public profile details for this staff member."
                  label="Staff Profile"
                  maxWidth={280}
                />
              )}
            </div>
            <p className="text-sm md:text-base text-slate-500">
              These fields belong to the staff profile.
            </p>
            {showInlineHelp && (
              <p className={`${tabTipClass} mt-3 mb-0`}>
                Tip: Add only business-relevant profile notes and avoid private or sensitive personal details.
              </p>
            )}
          </div>
        </div>

        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-base md:text-lg font-semibold text-slate-900">
              Skills
              {showInlineHelp && (
                <InfoTooltip
                  text="Services or strengths this staff member is known for."
                  label="Skills"
                  maxWidth={260}
                />
              )}
            </span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
            {SKILL_OPTIONS.map((skill) => {
              const checked = form.skills.includes(skill);
              const skillId = `staff-skill-${skill.replace(/\s+/g, "-").toLowerCase()}`;
              return (
                <label
                  key={skill}
                  htmlFor={skillId}
                  className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm md:text-base ${
                    checked
                      ? "border-slate-900 bg-slate-50 text-slate-900"
                      : "border-slate-200 bg-white text-slate-600"
                  }`}
                >
                  <input
                    id={skillId}
                    name="staff_skills[]"
                    type="checkbox"
                    value={skill}
                    checked={checked}
                    onChange={() => toggleSelection("skills", skill)}
                    className="h-4 w-4 rounded border border-slate-300 text-slate-900 focus:ring-0"
                  />
                  <span className="leading-tight">{skill}</span>
                </label>
              );
            })}
          </div>
          {form.skills.includes("Other") && (
            <div className="grid gap-3">
              <label htmlFor="staff_skills_other" className="flex flex-col gap-1">
                <span className={`${labelText} uppercase`}>Other skills</span>
                <input
                  id="staff_skills_other"
                  name="staff_skills_other"
                  type="text"
                  value={form.skills_other}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, skills_other: e.target.value }))
                  }
                  className={`w-full ${fieldClass} ${focusClass}`.trim()}
                />
              </label>
            </div>
          )}
        </section>

        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-base md:text-lg font-semibold text-slate-900">
              Languages
              {showInlineHelp && (
                <InfoTooltip
                  text="Languages this staff member can use with clients."
                  label="Languages"
                  maxWidth={260}
                />
              )}
            </span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
            {LANGUAGE_OPTIONS.map((language) => {
              const checked = form.languages.includes(language);
              const langId = `staff-language-${language.replace(/\s+/g, "-").toLowerCase()}`;
              return (
                <label
                  key={language}
                  htmlFor={langId}
                  className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm md:text-base ${
                    checked
                      ? "border-slate-900 bg-slate-50 text-slate-900"
                      : "border-slate-200 bg-white text-slate-600"
                  }`}
                >
                  <input
                    id={langId}
                    name="staff_languages[]"
                    type="checkbox"
                    value={language}
                    checked={checked}
                    onChange={() => toggleSelection("languages", language)}
                    className="h-4 w-4 rounded border border-slate-300 text-slate-900 focus:ring-0"
                  />
                  <span className="leading-tight">{language}</span>
                </label>
              );
            })}
          </div>
          {form.languages.includes("Other") && (
            <div className="grid gap-3">
              <label htmlFor="staff_languages_other" className="flex flex-col gap-1">
                <span className={`${labelText} uppercase`}>Other languages</span>
                <input
                  id="staff_languages_other"
                  name="staff_languages_other"
                  type="text"
                  value={form.languages_other}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, languages_other: e.target.value }))
                  }
                  className={`w-full ${fieldClass} ${focusClass}`.trim()}
                />
              </label>
            </div>
          )}
        </section>

        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-base md:text-lg font-semibold text-slate-900">
              Years of experience
              {showInlineHelp && (
                <InfoTooltip
                  text="How long this staff member has worked in this field."
                  label="Years of experience"
                  maxWidth={260}
                />
              )}
            </span>
          </div>
          <div className="space-y-2">
            {YEARS_OPTIONS.map((option) => {
              const checked = form.years_experience === option;
              const optionId = `staff-years-experience-${option.replace(/\s+/g, "-").toLowerCase()}`;
              return (
                <label
                  key={option}
                  htmlFor={optionId}
                  className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2 text-sm md:text-base ${
                    checked
                      ? "border-slate-900 bg-slate-50 text-slate-900"
                      : "border-slate-200 bg-white text-slate-600"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <input
                      id={optionId}
                      name="staff_years_experience"
                      type="radio"
                      value={option}
                      checked={checked}
                      onChange={() =>
                        setForm((f) => ({ ...f, years_experience: option }))
                      }
                      className="h-4 w-4 rounded-full border border-slate-300 text-slate-900 focus:ring-0"
                    />
                    <span>{option}</span>
                  </span>
                  {checked && (
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                      Selected
                    </span>
                  )}
                </label>
              );
            })}
          </div>
          {form.years_experience === "Other" && (
            <div className="grid gap-3">
              <label htmlFor="staff_years_experience_other" className="flex flex-col gap-1">
                <span className={`${labelText} uppercase`}>Other experience</span>
                <input
                  id="staff_years_experience_other"
                  name="staff_years_experience_other"
                  type="text"
                  value={form.years_experience_other}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, years_experience_other: e.target.value }))
                  }
                  className={`w-full ${fieldClass} ${focusClass}`.trim()}
                />
              </label>
            </div>
          )}
        </section>

        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-base md:text-lg font-semibold text-slate-900">
              Certification
              {showInlineHelp && (
                <InfoTooltip
                  text="Shows whether this staff member is certified."
                  label="Certification"
                  maxWidth={260}
                />
              )}
            </span>
          </div>
          <div className="flex flex-wrap gap-3 text-sm md:text-base">
            {CERTIFICATION_OPTIONS.map((option) => {
              const checked = form.is_certified === option.value;
              const certId = `staff-certification-${String(option.value)}`;
              return (
                <label
                  key={option.label}
                  htmlFor={certId}
                  className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${
                    checked
                      ? "border-slate-900 bg-slate-50 text-slate-900"
                      : "border-slate-200 bg-white text-slate-600"
                  }`}
                >
                  <input
                    id={certId}
                    name="staff_is_certified"
                    type="radio"
                    value={option.value}
                    checked={checked}
                    onChange={() =>
                      setForm((f) => ({ ...f, is_certified: option.value }))
                    }
                    className="h-4 w-4 rounded-full border border-slate-300 text-slate-900 focus:ring-0"
                  />
                  <span>{option.label}</span>
                </label>
              );
            })}
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-base md:text-lg font-semibold text-slate-900">
              Notes
              {showInlineHelp && (
                <InfoTooltip
                  text="Internal notes for managers."
                  label="Notes"
                  maxWidth={240}
                />
              )}
            </span>
          </div>
          <textarea
            id="staff_notes"
            name="staff_notes"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            className={textareaClass}
          />
        </section>
      </div>

      <div id="section-schedule" className={sectionClassName("section-schedule")}>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className={sectionTitleClass}>Weekly Schedule</h2>
          {showInlineHelp && (
            <InfoTooltip
              text="Working hours are used to determine availability on the schedule."
              label="Working Hours"
              maxWidth={260}
            />
          )}
          {showInlineHelp && (
            <InfoTooltip
              text="Time off blocks scheduling and prevents ticket service assignment during blocked times."
              label="Time Off"
              maxWidth={260}
            />
          )}
        </div>
        {showInlineHelp && (
          <p className={`${tabTipClass} mt-4`}>
            Tip: Keep weekly hours and time-off entries current to avoid invalid ticket technician assignments.
          </p>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full weekly-schedule-table">
            <thead>
              <tr className="text-left text-slate-600">
                <th className={`${labelText} uppercase weekly-schedule-col-day`}>Day</th>
                <th className={`${labelText} uppercase weekly-schedule-col-working`}>
                  <span className="flex items-center gap-2">
                    Working?
                    {showInlineHelp && (
                      <InfoTooltip
                        text="Turn on the days this staff member works."
                        label="Working"
                        maxWidth={240}
                      />
                    )}
                  </span>
                </th>
                <th className={`${labelText} uppercase weekly-schedule-col-start`}>
                  <span className="flex items-center gap-2">
                    Start
                    {showInlineHelp && (
                      <InfoTooltip
                        text="First available time for this day."
                        label="Start time"
                        maxWidth={240}
                      />
                    )}
                  </span>
                </th>
                <th className={`${labelText} uppercase weekly-schedule-col-end`}>
                  <span className="flex items-center gap-2">
                    End
                    {showInlineHelp && (
                      <InfoTooltip
                        text="Last available time for this day."
                        label="End time"
                        maxWidth={240}
                      />
                    )}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {weeklySchedule.map((item) => (
                <tr key={item.day_of_week} className="border-t border-slate-200">
                  <td className="font-semibold text-slate-900 weekly-schedule-col-day">
                    {DAYS[item.day_of_week] || `Day ${item.day_of_week}`}
                  </td>
                  <td className="weekly-schedule-col-working">
                    <input
                      id={`staff-schedule-${item.day_of_week}-working`}
                      name={`staff_schedule[${item.day_of_week}][is_working]`}
                      type="checkbox"
                      checked={!!item.is_working}
                      onChange={(e) =>
                        handleToggleWorking(item.day_of_week, e.target.checked)
                      }
                      className="h-4 w-4 rounded border border-slate-300 text-slate-900 focus:ring-0"
                    />
                  </td>
                  <td className="weekly-schedule-col-start">
                    <TimeSelect
                      value={item.start_time || "09:00"}
                      disabled={!item.is_working}
                      onChange={(val) =>
                        handleScheduleChange(item.day_of_week, "start_time", snapTo15(val))
                      }
                      className="time-select-group"
                      size={hasUiClasses ? "lg" : undefined}
                      compact
                      idPrefix={`staff-schedule-${item.day_of_week}-start`}
                      namePrefix={`staff_schedule[${item.day_of_week}][start_time]`}
                    />
                  </td>
                  <td className="weekly-schedule-col-end">
                    <TimeSelect
                      value={item.end_time || "17:00"}
                      disabled={!item.is_working}
                      onChange={(val) =>
                        handleScheduleChange(item.day_of_week, "end_time", snapTo15(val))
                      }
                      className="time-select-group"
                      size={hasUiClasses ? "lg" : undefined}
                      compact
                      idPrefix={`staff-schedule-${item.day_of_week}-end`}
                      namePrefix={`staff_schedule[${item.day_of_week}][end_time]`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {typeof onSaveSchedule === "function" && (
          <button
            type="button"
            onClick={handleScheduleSave}
            disabled={savingSchedule}
            className={primaryBtnClass}
          >
            {savingSchedule ? "Saving..." : "Save Schedule"}
          </button>
        )}
      </div>
    </div>
  );

}
